/**
 * Multi-tab safety coordinator (Polish Phase 2).
 *
 * The builder persists to a single shared IndexedDB autosave slot, so two tabs
 * open at once would silently clobber each other. This guard elects exactly one
 * "primary" (writer) tab; every other tab is "secondary" (read-only) and must
 * suppress its autosave. Pure coordination — it never touches the store or DOM
 * directly; callers react via the role callbacks and the beforeYield hook.
 *
 * Primary path: the Web Locks API (an exclusive `pb-builder` lock the primary
 * holds for its lifetime; later tabs queue behind it and auto-promote when it
 * releases or its tab closes). Fallback: a BroadcastChannel heartbeat election.
 *
 * All environment access goes through the injected `win` so the state machine is
 * headless-testable (mock navigator.locks + mock BroadcastChannel), mirroring the
 * `initUnloadGuard(store, {doc, win})` pattern from Phase 1.
 */

const ELECTION_WAIT_MS = 300;
const HEARTBEAT_INTERVAL_MS = 1000;
const HEARTBEAT_TIMEOUT_MS = 3000;
// After the watchdog observes a stale primary it does NOT promote directly — it
// pings and waits this long for a reply. Message dispatch is not timer-throttled
// the way setInterval is in a backgrounded tab, so a merely-throttled (alive)
// primary answers the ping well within the grace window and no flap occurs; only
// a genuinely dead primary stays silent and triggers promotion.
const PING_GRACE_MS = 1000;

/**
 * @param {object} opts
 * @param {Window} [opts.win] - environment (navigator, BroadcastChannel, timers)
 * @param {string} [opts.lockName]
 * @param {string} [opts.channelName]
 * @param {() => void} [opts.onBecomePrimary]
 * @param {(info: {reason: 'init'|'yielded'}) => void} [opts.onBecomeSecondary]
 * @param {() => (void|Promise<void>)} [opts.onBeforeYield] - runs while still writable,
 *        before the lock is released to a taker (flush point)
 * @returns {{ start(): Promise<void>, requestTakeover(): void, getRole(): string, dispose(): void }}
 */
export function createTabGuard({
  win = typeof window !== 'undefined' ? window : undefined,
  lockName = 'pb-builder',
  channelName = 'pb-tab-guard',
  onBecomePrimary = () => {},
  onBecomeSecondary = () => {},
  onBeforeYield = () => {}
} = {}) {
  if (!win) throw new Error('createTabGuard requires a window-like environment');

  const locks = win.navigator && win.navigator.locks;
  const useLocks = !!(locks && typeof locks.request === 'function');
  const BC = win.BroadcastChannel;
  const hasChannel = typeof BC === 'function';

  const tabId =
    win.crypto && typeof win.crypto.randomUUID === 'function'
      ? win.crypto.randomUUID()
      : `tab-${Math.random().toString(36).slice(2)}`;

  let role = 'acquiring';
  let disposed = false;
  let channel = hasChannel ? new BC(channelName) : null;

  // Web Locks state
  let releaseLock = null; // resolve() that frees the currently-held lock
  let yielding = false;

  // Fallback election state
  let heartbeatTimer = null;
  let watchdogTimer = null;
  let electionTimer = null;
  let lastHeartbeatAt = 0;
  let pendingTakeoverId = null;
  let pendingTakeoverTimer = null;
  let fallbackYielding = false;
  let pingGraceTimer = null; // in-flight watchdog ping grace window (null = none)

  function setRole(next, info) {
    if (disposed || role === next) return;
    role = next;
    if (next === 'primary') onBecomePrimary();
    else if (next === 'secondary') onBecomeSecondary(info || { reason: 'init' });
  }

  function postMessage(msg) {
    if (channel) {
      try {
        channel.postMessage({ ...msg, from: tabId });
      } catch {
        /* channel closed */
      }
    }
  }

  // ── Web Locks primary path ──────────────────────────────────────────────

  function holdLock() {
    // The lock is held until releaseLock() is called (yield or dispose).
    return new Promise((resolve) => {
      releaseLock = () => {
        releaseLock = null;
        resolve();
      };
    });
  }

  function queueForPromotion() {
    // A normal exclusive request queues behind the current primary and its
    // callback fires only once that primary releases (or its tab closes),
    // auto-promoting this tab. FIFO makes the 2-tab takeover deterministic.
    locks
      .request(lockName, { mode: 'exclusive' }, async () => {
        if (disposed) return;
        setRole('primary');
        await holdLock();
      })
      .catch(() => {
        /* AbortError on dispose, or lock manager gone */
      });
  }

  async function acquireViaLocks() {
    let settled = false;
    await new Promise((resolveStart) => {
      const finishStart = () => {
        if (!settled) {
          settled = true;
          resolveStart();
        }
      };
      locks
        .request(lockName, { ifAvailable: true }, async (lock) => {
          if (disposed) return;
          if (lock) {
            setRole('primary');
            finishStart();
            await holdLock();
          } else {
            setRole('secondary', { reason: 'init' });
            finishStart();
            queueForPromotion();
          }
        })
        .catch(() => {
          finishStart();
        });
    });
  }

  async function yieldViaLocks() {
    if (yielding || role !== 'primary') return;
    yielding = true;
    try {
      await onBeforeYield(); // flush while still writable
    } catch {
      /* flush failure must not strand the lock */
    }
    setRole('secondary', { reason: 'yielded' }); // writable false before release
    if (releaseLock) releaseLock(); // FIFO hands the lock to the waiting taker
    yielding = false;
    if (!disposed) queueForPromotion(); // re-enter the queue for a future takeover
  }

  // ── BroadcastChannel fallback election ──────────────────────────────────

  function startHeartbeat() {
    stopWatchdog();
    postMessage({ type: 'heartbeat' });
    heartbeatTimer = win.setInterval(
      () => postMessage({ type: 'heartbeat' }),
      HEARTBEAT_INTERVAL_MS
    );
  }

  function stopHeartbeat() {
    if (heartbeatTimer != null) {
      win.clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  }

  function startWatchdog() {
    stopWatchdog();
    lastHeartbeatAt = now();
    watchdogTimer = win.setInterval(() => {
      if (role !== 'secondary') return;
      if (now() - lastHeartbeatAt <= HEARTBEAT_TIMEOUT_MS) return;
      if (pingGraceTimer != null) return; // a grace window is already in flight
      // Stale: don't promote yet. Ping the (possibly just-throttled) primary and
      // give it a grace window to answer before re-electing self. If it replies,
      // lastHeartbeatAt advances and the grace check below stands down.
      const heartbeatAtPing = lastHeartbeatAt;
      postMessage({ type: 'ping' });
      pingGraceTimer = win.setTimeout(() => {
        pingGraceTimer = null;
        if (disposed || role !== 'secondary') return;
        if (lastHeartbeatAt > heartbeatAtPing) return; // primary answered — stay secondary
        becomePrimaryFallback(); // still silent — re-elect self
      }, PING_GRACE_MS);
    }, HEARTBEAT_INTERVAL_MS);
  }

  function stopWatchdog() {
    if (watchdogTimer != null) {
      win.clearInterval(watchdogTimer);
      watchdogTimer = null;
    }
    if (pingGraceTimer != null) {
      win.clearTimeout(pingGraceTimer);
      pingGraceTimer = null;
    }
  }

  function clearPendingTakeover() {
    pendingTakeoverId = null;
    if (pendingTakeoverTimer != null) {
      win.clearTimeout(pendingTakeoverTimer);
      pendingTakeoverTimer = null;
    }
  }

  function now() {
    return typeof win.performance?.now === 'function' ? win.performance.now() : Date.now();
  }

  function becomePrimaryFallback() {
    clearPendingTakeover();
    stopWatchdog();
    setRole('primary');
    startHeartbeat();
    postMessage({ type: 'claim' });
  }

  function becomeSecondaryFallback(reason) {
    stopHeartbeat();
    setRole('secondary', { reason });
    startWatchdog();
  }

  async function acquireViaChannel() {
    if (!channel) {
      setRole('primary');
      return;
    } // no coordination possible — assume sole tab
    let heard = false;
    postMessage({ type: 'ping' });
    await new Promise((resolve) => {
      const onMsg = (ev) => {
        const d = ev.data;
        if (d && d.from !== tabId && (d.type === 'heartbeat' || d.type === 'claim')) heard = true;
      };
      channel.addEventListener
        ? channel.addEventListener('message', onMsg)
        : (channel.onmessage = onMsg);
      electionTimer = win.setTimeout(() => {
        channel.removeEventListener && channel.removeEventListener('message', onMsg);
        resolve();
      }, ELECTION_WAIT_MS);
    });
    if (disposed) return;
    if (heard) becomeSecondaryFallback('init');
    else becomePrimaryFallback();
  }

  // ── Shared channel message handling ─────────────────────────────────────

  function onChannelMessage(ev) {
    const d = ev.data;
    if (!d || d.from === tabId || disposed) return;

    if (d.type === 'takeover') {
      // Only the current writer yields.
      if (useLocks) {
        if (role === 'primary') void yieldViaLocks();
      } else if (role === 'primary' && !fallbackYielding) {
        fallbackYielding = true;
        void (async () => {
          try {
            await onBeforeYield();
          } catch {
            /* ignore */
          }
          fallbackYielding = false;
          becomeSecondaryFallback('yielded');
          postMessage({ type: 'yielded', requestId: d.requestId || null, to: d.from });
        })();
      }
      return;
    }

    if (!useLocks) {
      if (d.type === 'yielded') {
        if (d.to && d.to !== tabId) return;
        if (pendingTakeoverId && d.requestId === pendingTakeoverId && role !== 'primary') {
          becomePrimaryFallback();
        }
      } else if (d.type === 'ping' && role === 'primary') {
        postMessage({ type: 'heartbeat' });
      } else if (d.type === 'heartbeat' || d.type === 'claim') {
        if (role === 'primary') {
          // Two primaries — lowest id wins to avoid a split brain. Heartbeats
          // participate too, because delayed delivery can make both tabs elect
          // themselves before either has seen a claim. The loser flushes
          // (onBeforeYield, while still writable) before demoting so its dirty
          // state is not dropped on the transition.
          if (tabId > d.from && !fallbackYielding) {
            fallbackYielding = true;
            void (async () => {
              try {
                await onBeforeYield();
              } catch {
                /* ignore */
              }
              fallbackYielding = false;
              becomeSecondaryFallback('yielded');
            })();
          }
        } else {
          lastHeartbeatAt = now();
        }
      }
    }
  }

  // ── Public API ──────────────────────────────────────────────────────────

  async function start() {
    if (disposed) return;
    if (channel) {
      const handler = onChannelMessage;
      channel.addEventListener
        ? channel.addEventListener('message', handler)
        : (channel.onmessage = handler);
    }
    if (useLocks) await acquireViaLocks();
    else await acquireViaChannel();
  }

  function requestTakeover() {
    if (disposed || role === 'primary') return;
    if (useLocks) {
      postMessage({ type: 'takeover' });
    } else {
      // No FIFO arbiter is available, so wait for the current primary to flush
      // and explicitly yield before becoming writable. If the primary is gone,
      // the heartbeat watchdog will promote this tab after the timeout.
      const requestId = `${tabId}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      clearPendingTakeover();
      pendingTakeoverId = requestId;
      postMessage({ type: 'takeover', requestId });
      pendingTakeoverTimer = win.setTimeout(() => {
        pendingTakeoverTimer = null;
        if (pendingTakeoverId !== requestId || role === 'primary') return;
        if (now() - lastHeartbeatAt > HEARTBEAT_TIMEOUT_MS) {
          becomePrimaryFallback();
        }
      }, HEARTBEAT_TIMEOUT_MS + ELECTION_WAIT_MS);
    }
    // Web Locks path: our queued request (queueForPromotion) is granted once the
    // old primary releases, promoting us — no extra action needed.
  }

  function getRole() {
    return role;
  }

  function dispose() {
    if (disposed) return;
    disposed = true;
    stopHeartbeat();
    stopWatchdog();
    clearPendingTakeover();
    if (electionTimer != null) {
      win.clearTimeout(electionTimer);
      electionTimer = null;
    }
    if (releaseLock) releaseLock();
    if (channel) {
      try {
        channel.close();
      } catch {
        /* already closed */
      }
      channel = null;
    }
  }

  return { start, requestTakeover, getRole, dispose };
}
